import { useCallback, useEffect, useState } from "react";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "viny.cookie-consent.v1";
const OPEN_EVENT = "viny:open-cookie-preferences";

export type CookiePrefs = {
  necessary: true;
  analytics: boolean;
  ads: boolean;
};

const DEFAULT_PREFS: CookiePrefs = { necessary: true, analytics: true, ads: true };

function readPrefs(): CookiePrefs | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookiePrefs>;
    return { necessary: true, analytics: !!parsed.analytics, ads: !!parsed.ads };
  } catch {
    return null;
  }
}

function writePrefs(prefs: CookiePrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

/** Abre o painel de preferências de cookies de qualquer lugar do app. */
export function openCookiePreferences() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Botão sutil (ícone de cookie) para o rodapé. */
export function CookiePreferencesButton({ className }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={openCookiePreferences}
      aria-label="Preferências de cookies"
      className={cn(
        "inline-flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-black",
        className,
      )}
    >
      <Cookie className="h-3.5 w-3.5" />
      <span>Cookies</span>
    </button>
  );
}

/** Banner de consentimento + painel de configuração. */
export function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [prefs, setPrefs] = useState<CookiePrefs>(DEFAULT_PREFS);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const saved = readPrefs();
    if (saved) {
      setPrefs(saved);
      setShowBanner(false);
    } else {
      setShowBanner(true);
    }
  }, []);

  useEffect(() => {
    const onOpen = () => {
      setPrefs(readPrefs() ?? DEFAULT_PREFS);
      setPanelOpen(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  const save = useCallback((next: CookiePrefs) => {
    writePrefs(next);
    setPrefs(next);
    setShowBanner(false);
    setPanelOpen(false);
  }, []);

  return (
    <>
      {mounted && showBanner && (
        <div className="fixed inset-x-3 bottom-3 z-[70] md:inset-x-auto md:left-5 md:bottom-5 md:max-w-[420px]">
          <div className="rounded-2xl border border-white/10 bg-[#141414]/95 p-4 text-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.8)] backdrop-blur">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white/10">
                <Cookie className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold">Cookies por aqui</p>
                <p className="mt-1 text-xs leading-relaxed text-white/60">
                  Usamos cookies para manter você conectado, entender o uso da plataforma e
                  exibir anúncios. Você pode ajustar isso quando quiser.
                </p>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                className="h-9 rounded-full px-4 text-xs font-semibold"
                onClick={() => save({ necessary: true, analytics: true, ads: true })}
              >
                Aceitar todos
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-9 rounded-full border-white/20 bg-transparent px-4 text-xs font-semibold text-white hover:bg-white/10 hover:text-white"
                onClick={() => save({ necessary: true, analytics: false, ads: false })}
              >
                Só necessários
              </Button>
              <button
                type="button"
                onClick={() => setPanelOpen(true)}
                className="text-xs font-semibold text-white/70 underline-offset-4 hover:underline"
              >
                Personalizar
              </button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={panelOpen} onOpenChange={setPanelOpen}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Preferências de cookies</DialogTitle>
            <DialogDescription>
              Escolha o que a Viny Club pode usar durante sua navegação.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <PrefRow
              title="Necessários"
              description="Login, segurança e funcionamento básico. Sempre ativos."
              checked
              disabled
            />
            <PrefRow
              title="Análise de uso"
              description="Ajuda a entender quais páginas funcionam melhor."
              checked={prefs.analytics}
              onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
            />
            <PrefRow
              title="Anúncios"
              description="Permite anúncios mais relevantes para você."
              checked={prefs.ads}
              onChange={(v) => setPrefs((p) => ({ ...p, ads: v }))}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => save({ necessary: true, analytics: false, ads: false })}
            >
              Recusar opcionais
            </Button>
            <Button onClick={() => save(prefs)}>Salvar preferências</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-border p-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">{title}</p>
        <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{description}</p>
      </div>
      <Switch checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  );
}
